/**
 * SharedViewCard — View card on a shared project page.
 * Shows thumbnail with a centered Play overlay, and two large actions:
 * Preview (full-screen player) and Gallery (grid of photos).
 * Play either streams a fresh cached video or requests a new render.
 */

import { useState, useEffect, useMemo, useRef } from "react";
import { Spin, Button, Modal, Progress, Tag, Typography } from "antd";
import { MaterialIcon } from "@/components/MaterialIcon";
import { NetflixPlayer } from "@/features/player";
import type { View, Photo, VideoJob, ShareTimespan } from "@/types";
import {
  requestSharedVideo,
  subscribeToSharedVideoJob,
  findFreshCachedVideo,
  subscribeToSharedVideos,
} from "@/services/shareService";
import { getVideoStatusLabel, TIMESPAN_SECONDS } from "@/services/videoService";
import { formatTimeAgo } from "@/utils/dateUtils";
import { MODAL_BLUR_STYLES } from "@/styles/modalStyles";

const EMPTY_SET = new Set<string>();
const noopToggle = (_id: string, _idx: number, _shift: boolean) => {};
const isNeverSelected = (_id: string) => false;

const TIMESPAN_FRIENDLY: Record<ShareTimespan, string> = {
  "1h": "the last hour",
  "1d": "the last day",
  "3d": "the last 3 days",
  "1w": "the last week",
  "2w": "the last 2 weeks",
  "1m": "the last month",
};

const TIMESPAN_LABEL: Record<ShareTimespan, string> = {
  "1h": "1 hour",
  "1d": "1 day",
  "3d": "3 days",
  "1w": "1 week",
  "2w": "2 weeks",
  "1m": "1 month",
};

const TIMESPAN_ORDER: ShareTimespan[] = ["1h", "1d", "3d", "1w", "2w", "1m"];

const { Text } = Typography;

interface SharedViewCardProps {
  view: View;
  projectName: string;
  latestPhoto: Photo | null;
  viewPhotos: Photo[];
  viewPhotosLoading: boolean;
  shareId: string;
  shareToken: string;
  allowedTimespans: ShareTimespan[];
  galleryEnabled: boolean;
  videoEnabled: boolean;
  onOpenView: (viewId: string, mode: "grid" | "preview") => void;
}

export default function SharedViewCard({
  view,
  projectName: _projectName,
  latestPhoto: _latestPhoto,
  viewPhotos,
  viewPhotosLoading,
  shareId,
  shareToken,
  allowedTimespans,
  galleryEnabled,
  videoEnabled,
  onOpenView,
}: SharedViewCardProps) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const [videoModalOpen, setVideoModalOpen] = useState(false);
  const [videoJob, setVideoJob] = useState<VideoJob | null>(null);
  const [selectedTimespan, setSelectedTimespan] = useState<ShareTimespan | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [preparing, setPreparing] = useState(false);
  const videoElRef = useRef<HTMLVideoElement | null>(null);

  // NetflixPlayer expects photos in chronological order (oldest → newest)
  const playerPhotos = useMemo(() => [...viewPhotos].reverse(), [viewPhotos]);
  const playerTimezone = view.androidStatus?.device?.timezone;

  // List of completed videos for this (share, view), most recent first
  const [videos, setVideos] = useState<VideoJob[]>([]);
  useEffect(() => {
    const unsub = subscribeToSharedVideos(shareId, view.viewId, setVideos);
    return () => unsub();
  }, [shareId, view.viewId]);

  // Photos summary: up to 6 equispaced shots, oldest → newest
  const photosSummary = useMemo<Photo[]>(() => {
    if (viewPhotos.length === 0) return [];
    // viewPhotos is desc (newest first); reverse to chronological
    const chrono = [...viewPhotos].reverse();
    if (chrono.length <= 6) return chrono;
    const result: Photo[] = [];
    for (let i = 0; i < 6; i++) {
      const idx = Math.round((i * (chrono.length - 1)) / 5);
      result.push(chrono[idx]);
    }
    return result;
  }, [viewPhotos]);

  const openGrid = () => onOpenView(view.viewId, "grid");

  // Subscribe to active video job
  useEffect(() => {
    if (!videoJob?.jobId) return;
    if (videoJob.status === "completed" || videoJob.status === "failed") return;

    const unsub = subscribeToSharedVideoJob(videoJob.jobId, (job) => {
      if (job) setVideoJob(job);
    });
    return () => unsub();
  }, [videoJob?.jobId, videoJob?.status]);

  const totalPhotos = view.stats?.photoCount ?? 0;
  const viewLastPhotoMs = view.stats?.lastPhotoAt?.toMillis?.() ?? 0;

  const handlePlayClick = () => {
    if (totalPhotos === 0) return;
    if (allowedTimespans.length === 0) return;
    if (allowedTimespans.length === 1) {
      generateFor(allowedTimespans[0]);
      return;
    }
    setPickerOpen(true);
  };

  const openExistingVideo = (job: VideoJob) => {
    setSelectedTimespan(((job as any).timespan as ShareTimespan) ?? null);
    setError(null);
    setVideoJob(job);
    setVideoModalOpen(true);
  };

  const formatVideoDate = (job: VideoJob): string => {
    const ms = (job as any).createdAt?.toMillis?.() ?? 0;
    if (!ms) return "";
    return new Date(ms).toLocaleDateString(undefined, {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const generateFor = async (timespan: ShareTimespan) => {
    setPickerOpen(false);
    setSelectedTimespan(timespan);
    setError(null);
    setVideoModalOpen(true);
    setPreparing(true);

    try {
      const cached = await findFreshCachedVideo(
        shareId,
        view.viewId,
        timespan,
        viewLastPhotoMs
      );

      if (cached) {
        setVideoJob(cached);
        return;
      }

      const result = await requestSharedVideo(shareToken, view.viewId, timespan);
      setVideoJob({
        jobId: result.jobId,
        status: "pending",
        progress: 0,
        photoCount: result.photoCount,
      } as VideoJob);
    } catch (err: unknown) {
      const raw =
        err instanceof Error ? err.message : "Failed to create video";
      const match = raw.match(/\(([^)]+)\)$/);
      const serverMsg = match ? match[1] : raw;

      // Friendlier copy for the common "no photos in range" error
      let friendly = serverMsg;
      if (/no photos/i.test(serverMsg)) {
        const lastPhotoAt = view.stats?.lastPhotoAt?.toDate?.();
        const rangeLabel = TIMESPAN_FRIENDLY[timespan] ?? "the selected time range";
        friendly = lastPhotoAt
          ? `No new photos in ${rangeLabel}. The most recent photo is from ${formatTimeAgo(lastPhotoAt)}.`
          : `No photos available in ${rangeLabel} yet.`;
      }

      setError(friendly);
    } finally {
      setPreparing(false);
    }
  };

  const closeVideoModal = () => {
    setVideoModalOpen(false);
    if (videoElRef.current) {
      videoElRef.current.pause();
    }
    setVideoJob(null);
    setSelectedTimespan(null);
    setError(null);
  };

  const isJobActive =
    videoJob &&
    ["pending", "downloading", "encoding", "uploading"].includes(videoJob.status);
  const isJobCompleted =
    videoJob && videoJob.status === "completed" && videoJob.downloadUrl;
  const isJobFailed = videoJob && videoJob.status === "failed";

  return (
    <div>
      {galleryEnabled && (
      <>
      {/* NetflixPlayer as the main block — full container width.
          The player's CSS module sizes its container to the viewport, so we
          override height/min-height. The attribute selector matches the
          hashed CSS module class. The project name + time are overlaid on
          the top-right corner of the player. */}
      <div className="shared-inline-player">
        <style>{`
          .shared-inline-player [class*="playerContainer"] {
            height: calc(100vh - 220px) !important;
            min-height: 320px !important;
            max-height: 720px !important;
          }
          .shared-inline-player [class*="loadingContainer"] {
            height: calc(100vh - 220px) !important;
            min-height: 320px !important;
            max-height: 720px !important;
          }
          /* Compact speed button bar */
          .shared-inline-player [class*="speedButton"] {
            min-width: 36px !important;
            height: 28px !important;
            font-size: 11px !important;
            padding: 0 6px !important;
            border-radius: 4px !important;
          }
          .shared-inline-player [class*="speedButtonBar"] {
            gap: 2px !important;
          }
          .shared-inline-player [class*="speedButtonsGroup"] {
            gap: 1px !important;
          }
          .shared-inline-player [class*="pauseButton"] {
            margin: 0 4px !important;
          }
          .shared-inline-player [class*="pauseButton"] svg {
            width: 22px !important;
            height: 22px !important;
          }
          /* Compact bottom controls bar */
          .shared-inline-player [class*="controls"] {
            padding: 4px 12px 6px !important;
            gap: 4px !important;
          }
          .shared-inline-player [class*="instructions"] {
            font-size: 10px !important;
          }
          /* Mobile-specific behaviour (drop ±12, shrink buttons, hide
             the title overlay) is now defined in the player's own CSS
             modules so it applies to every NetflixPlayer instance. */
        `}</style>
        <NetflixPlayer
          photos={playerPhotos}
          loading={viewPhotosLoading && playerPhotos.length === 0}
          selectedIds={EMPTY_SET}
          onPhotoClick={() => {}}
          onSelectionToggle={noopToggle}
          isSelected={isNeverSelected}
          timezone={playerTimezone}
          title="Preview"
          initialVelocity={0}
        />
      </div>

      {/* Photos section — equispaced summary (oldest → newest) + View all */}
      <div style={{ marginTop: 24 }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: 12,
          }}
        >
          <Text strong style={{ fontSize: 18 }}>
            Photos
          </Text>
          <Button
            type="primary"
            icon={<MaterialIcon name="arrow_forward" size={18} />}
            disabled={viewPhotos.length === 0}
            onClick={openGrid}
          >
            View all
          </Button>
        </div>

        {photosSummary.length === 0 ? (
          <Text type="secondary" style={{ fontSize: 13 }}>
            {viewPhotosLoading ? "Loading…" : "No photos yet."}
          </Text>
        ) : (
          <div className="shared-photos-grid">
            <style>{`
              .shared-photos-grid {
                display: grid;
                gap: 8px;
                /* Default (desktop): one row of N */
                grid-template-columns: repeat(${photosSummary.length}, 1fr);
              }
              /* Medium: 3 columns × 2 rows */
              @media (max-width: 900px) {
                .shared-photos-grid {
                  grid-template-columns: repeat(3, 1fr);
                }
              }
              /* Mobile: 2 columns × 3 rows */
              @media (max-width: 600px) {
                .shared-photos-grid {
                  grid-template-columns: repeat(2, 1fr);
                }
              }
            `}</style>
            {photosSummary.map((p) => {
              const captured = p.capturedAt?.toDate?.() ?? null;
              const dateLabel = captured
                ? captured.toLocaleDateString(undefined, {
                    day: "2-digit",
                    month: "short",
                    year: "numeric",
                  })
                : "";
              return (
                <button
                  key={p.photoId}
                  type="button"
                  onClick={openGrid}
                  aria-label={`Open gallery — ${dateLabel}`}
                  style={{
                    border: "none",
                    padding: 0,
                    background: "transparent",
                    cursor: "pointer",
                    display: "flex",
                    flexDirection: "column",
                    gap: 6,
                    minWidth: 0,
                  }}
                >
                  <div
                    style={{
                      width: "100%",
                      aspectRatio: "4 / 3",
                      borderRadius: 6,
                      overflow: "hidden",
                      background:
                        "radial-gradient(ellipse at center, #e0e0e0 0%, #9e9e9e 100%)",
                    }}
                  >
                    <img
                      src={p.urls.thumb400 || p.urls.thumb200}
                      alt=""
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        display: "block",
                      }}
                    />
                  </div>
                  <span
                    style={{
                      fontSize: 12,
                      color: "#8c8c8c",
                      textAlign: "center",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                    title={dateLabel}
                  >
                    {dateLabel}
                  </span>
                </button>
              );
            })}
          </div>
        )}
      </div>
      </>
      )}

      {videoEnabled && (
      <>
      {/* Videos section — generate button + list of completed videos */}
      <div style={{ marginTop: 24 }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: 12,
          }}
        >
          <Text strong style={{ fontSize: 18 }}>
            Videos
          </Text>
          <Button
            type="primary"
            icon={<MaterialIcon name="movie_filter" size={18} />}
            disabled={totalPhotos === 0}
            onClick={handlePlayClick}
          >
            Generate video
          </Button>
        </div>

        {videos.length === 0 ? (
          <Text type="secondary" style={{ fontSize: 13 }}>
            No videos yet.
          </Text>
        ) : (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 8,
            }}
          >
            {videos.map((job) => {
              const ts = (job as any).timespan as ShareTimespan | undefined;
              const tsLabel = ts ? TIMESPAN_LABEL[ts] ?? ts : "Video";
              return (
                <button
                  key={job.jobId}
                  type="button"
                  onClick={() => openExistingVideo(job)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    padding: "10px 12px",
                    border: "1px solid #f0f0f0",
                    borderRadius: 6,
                    background: "#fff",
                    cursor: "pointer",
                    textAlign: "left",
                  }}
                >
                  {/* First-frame poster with play icon overlay */}
                  <div
                    style={{
                      position: "relative",
                      width: 96,
                      height: 54,
                      flexShrink: 0,
                      borderRadius: 4,
                      overflow: "hidden",
                      background: "#000",
                    }}
                  >
                    {job.downloadUrl && (
                      <video
                        src={`${job.downloadUrl}#t=0.1`}
                        muted
                        playsInline
                        preload="metadata"
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                          pointerEvents: "none",
                          display: "block",
                        }}
                      />
                    )}
                    <div
                      style={{
                        position: "absolute",
                        inset: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        pointerEvents: "none",
                        background:
                          "linear-gradient(rgba(0,0,0,0) 0%, rgba(0,0,0,0.25) 100%)",
                      }}
                    >
                      <span
                        style={{
                          width: 32,
                          height: 32,
                          borderRadius: "50%",
                          background: "rgba(0,0,0,0.55)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          boxShadow:
                            "0 0 0 1.5px rgba(255,255,255,0.85)",
                        }}
                      >
                        <MaterialIcon
                          name="play_arrow"
                          size={20}
                          style={{ color: "#fff", marginLeft: 2 }}
                        />
                      </span>
                    </div>
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        fontSize: 14,
                        fontWeight: 500,
                        color: "#262626",
                      }}
                    >
                      {tsLabel}
                    </div>
                  </div>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {formatVideoDate(job)}
                  </Text>
                </button>
              );
            })}
          </div>
        )}
      </div>
      </>
      )}

      {/* Inline error feedback */}
      {error && !videoModalOpen && (
        <Text type="danger" style={{ fontSize: 12, display: "block", marginTop: 8 }}>
          {error}
        </Text>
      )}

      {/* Timespan picker Modal — shown before video generation */}
      <Modal
        open={pickerOpen}
        onCancel={() => setPickerOpen(false)}
        footer={null}
        width={420}
        centered
        destroyOnClose
        styles={MODAL_BLUR_STYLES}
        title="Choose the time range for your video"
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 8 }}>
          {TIMESPAN_ORDER.filter((ts) => allowedTimespans.includes(ts)).map((ts) => {
            const tsSec = TIMESPAN_SECONDS[ts] ?? 0;
            const lastPhotoMs = view.stats?.lastPhotoAt?.toMillis?.() ?? 0;
            const hasPhotosInRange =
              lastPhotoMs > 0 && (Date.now() - lastPhotoMs) / 1000 <= tsSec;
            return (
              <Button
                key={ts}
                block
                size="large"
                disabled={!hasPhotosInRange}
                style={{
                  textAlign: "left",
                  justifyContent: "flex-start",
                  height: 48,
                  fontWeight: 500,
                }}
                onClick={() => generateFor(ts)}
              >
                {TIMESPAN_LABEL[ts] ?? ts}
              </Button>
            );
          })}
        </div>
      </Modal>

      {/* Video lightbox Modal */}
      <Modal
        open={videoModalOpen}
        onCancel={closeVideoModal}
        footer={null}
        width={720}
        centered
        destroyOnClose
        styles={MODAL_BLUR_STYLES}
        title={
          selectedTimespan
            ? `${view.name} — ${TIMESPAN_LABEL[selectedTimespan] ?? selectedTimespan}`
            : view.name
        }
      >
        <div
          style={{
            minHeight: 240,
            display: "flex",
            flexDirection: "column",
            alignItems: "stretch",
            justifyContent: "center",
            gap: 12,
          }}
        >
          {(preparing || isJobActive) && (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 16,
                padding: 24,
              }}
            >
              <Spin size="large" />
              <Text strong>Creating your video…</Text>
              {videoJob && isJobActive && (
                <>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {getVideoStatusLabel(videoJob.status)}
                  </Text>
                  <div style={{ width: "100%" }}>
                    <Progress
                      percent={videoJob.progress}
                      size="small"
                      status="active"
                    />
                  </div>
                </>
              )}
              {preparing && !videoJob && (
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Checking for existing videos…
                </Text>
              )}
            </div>
          )}

          {isJobCompleted && (
            <>
              <video
                ref={videoElRef}
                src={videoJob!.downloadUrl}
                controls
                autoPlay
                style={{
                  width: "100%",
                  maxHeight: "70vh",
                  background: "#000",
                  borderRadius: 4,
                }}
              />
              <div style={{ display: "flex", justifyContent: "flex-end" }}>
                <Button
                  icon={<MaterialIcon name="download" size={16} />}
                  href={videoJob!.downloadUrl}
                  target="_blank"
                >
                  Download
                </Button>
              </div>
            </>
          )}

          {isJobFailed && (
            <Tag color="error" style={{ whiteSpace: "normal", lineHeight: 1.4 }}>
              {videoJob!.error || "Video generation failed"}
            </Tag>
          )}

          {error && !isJobActive && !isJobCompleted && !preparing && (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 12,
                padding: 24,
                textAlign: "center",
              }}
            >
              <MaterialIcon
                name="schedule"
                size={40}
                style={{ color: "#8c8c8c" }}
              />
              <Text style={{ fontSize: 14, maxWidth: 420 }}>{error}</Text>
              <Button onClick={closeVideoModal}>Close</Button>
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
}
